import type { AllocationDraft } from './allocation.js';

/**
 * O rateio de centro de custo em várias linhas (#6697): as regras PURAS que o
 * `AllocationListField` aplica ao adicionar, remover e avisar. Quem trava o
 * envio é a regra do host (`costCenterSplitIssue`, no domínio); aqui só mora o
 * que a tela precisa para se mexer e para dizer o porquê.
 */

/** O teto de linhas, o mesmo `MAX_COST_CENTER_SPLIT_ROWS` do domínio (o kit não
 *  importa o domínio) e o `_kMaxAllocation` do legado. */
export const MAX_ALLOCATION_ROWS = 10;

/**
 * 100% em partes inteiras iguais, com a sobra na 1ª linha: 3 linhas dão 34/33/33,
 * 6 dão 20/16/16/16/16/16. A soma é sempre 100 — o legado arredondava cada parte
 * para baixo e chegava a 96% com 6 linhas.
 */
export function splitEvenly(count: number): number[] {
  if (count <= 0) return [];
  const part = Math.floor(100 / count);
  return Array.from({ length: count }, (_, i) => (i === 0 ? 100 - part * (count - 1) : part));
}

/** A linha com outro percentual; `undefined` tira o percentual (a linha
 *  sozinha, que vale 100%). */
export function withPercent(row: AllocationDraft, percent: number | undefined): AllocationDraft {
  const next: AllocationDraft = {
    companyId: row.companyId,
    costCenterId: row.costCenterId,
    projectId: row.projectId,
    ...(row.costCenterOption ? { costCenterOption: row.costCenterOption } : {}),
  };
  return percent === undefined ? next : { ...next, percent };
}

/** Redistribui os percentuais em partes iguais. Uma linha sozinha volta sem
 *  percentual: vale 100%, e o campo some, como antes do rateio. */
function withEvenSplit(rows: readonly AllocationDraft[]): AllocationDraft[] {
  if (rows.length === 1) return rows.map((row) => withPercent(row, undefined));
  const parts = splitEvenly(rows.length);
  return rows.map((row, i) => withPercent(row, parts[i]));
}

/** Uma linha nova, com a empresa da 1ª e o centro de custo em branco: copiar o
 *  centro de custo (como o legado fazia) já nasceria repetido. */
export function addAllocationRow(rows: readonly AllocationDraft[]): AllocationDraft[] {
  if (rows.length >= MAX_ALLOCATION_ROWS) return [...rows];
  const companyId = rows[0]?.companyId ?? '';
  return withEvenSplit([...rows, { companyId, costCenterId: '', projectId: '' }]);
}

/** Tira a linha `index` e divide 100% de novo entre as que ficam. A 1ª linha
 *  não sai: é a do viajante, como no legado. */
export function removeAllocationRow(
  rows: readonly AllocationDraft[],
  index: number,
): AllocationDraft[] {
  if (index <= 0 || index >= rows.length) return [...rows];
  return withEvenSplit(rows.filter((_, i) => i !== index));
}

/** O que a tela diz sob as linhas quando a divisão não fecha. A ordem segue o
 *  que o viajante resolve primeiro: o percentual da linha, a soma, o repetido. */
export type AllocationSplitProblem = 'zero_percent' | 'sum' | 'repeated';

/** O problema visível do rateio, ou `undefined`. Uma linha só nunca tem: sem
 *  percentual, ela vale 100%. Soma em centésimos inteiros — `33.33 + 33.33 +
 *  33.34` em ponto flutuante não dá exatamente 100. */
export function allocationSplitProblem(
  rows: readonly AllocationDraft[],
): AllocationSplitProblem | undefined {
  if (rows.length <= 1) return undefined;
  const cents = rows.map((row) => Math.round((row.percent ?? 0) * 100));
  if (cents.some((value) => value <= 0)) return 'zero_percent';
  if (cents.reduce((sum, value) => sum + value, 0) !== 10_000) return 'sum';
  const ids = rows.map((row) => row.costCenterId).filter((id) => id !== '');
  return new Set(ids).size === ids.length ? undefined : 'repeated';
}

/** O texto digitado no percentual: dígitos e uma vírgula (ou ponto) com até 2
 *  casas, como a máscara do legado (`^[0-9]*,?[0-9]{0,2}`). `null` ⇒ o texto não
 *  é aceito e o campo fica como estava. */
export function parsePercentInput(
  text: string,
): { text: string; value: number | undefined } | null {
  if (!/^\d{0,3}([.,]\d{0,2})?$/.test(text)) return null;
  const value = text === '' ? undefined : Number(text.replace(',', '.'));
  if (value !== undefined && (Number.isNaN(value) || value > 100)) return null;
  return { text, value };
}
